<div class="product-line" [attr.data-testid]="testid">
  <div class="product-line__head">
    <app-status-pill
      [status]="pillStatus"
      [label]="pillLabel"
      [testid]="testid + '-status'"
    ></app-status-pill>
    <span class="product-line__title">{{ product.title }}</span>
  </div>

  <!-- Task 2: action matrix — status-conditional inline controls, keyed off the
       proposal status. Writes reproduce the legacy proposal-details flows via
       ProposalService / SiteService and refresh the list through ProjectService.
       Task 3: entirely suppressed in read-only mode (Project History) — no
       bid inputs / accept-decline / ATP / submit, ever. -->
  <ng-container *ngIf="!readOnly">
  <div class="product-line__actions">
    <!-- INVITATION → inline quote form: price + complete-by + Submit / Decline.
         (A fixed-price direct offer arrives as OFFERED — handled below.) -->
    <div class="pl-quote" *ngIf="isInvitation">
      <div class="pl-quote__fields">
        <div class="form-field pl-quote__field">
          <label class="form-label" [attr.for]="tid('price-input')">
            {{ isFixedPriced ? "Offer Amount" : "Bid Amount" }}
          </label>
          <div class="pl-quote__money">
            <span class="pl-quote__money-prefix" aria-hidden="true">$</span>
            <input
              class="input pl-quote__money-input"
              type="number"
              [id]="tid('price-input')"
              [attr.data-testid]="tid('price-input')"
              placeholder="0"
              [(ngModel)]="bidAmount"
              [readonly]="productLocked"
              [matTooltip]="lockedReason"
              [matTooltipDisabled]="!productLocked"
              matTooltipTouchGestures="off"
              (ngModelChange)="onPriceChange()"
              (blur)="savePrice()"
            />
          </div>
        </div>

        <div class="form-field pl-quote__field">
          <label class="form-label" [attr.for]="tid('complete-by')">
            {{ isFixedPriced || isDesiredByDate ? "Due Date" : "Turn Time" }}
          </label>
          <input
            *ngIf="!isDesiredByDate"
            class="input"
            type="number"
            [id]="tid('complete-by')"
            [attr.data-testid]="tid('complete-by')"
            placeholder="Business Days"
            [(ngModel)]="turnTime"
            [readonly]="productLocked"
            [matTooltip]="lockedReason"
            [matTooltipDisabled]="!productLocked"
            matTooltipTouchGestures="off"
            (ngModelChange)="onTurnTimeChange()"
            (blur)="saveTurnTime()"
          />
          <input
            *ngIf="isDesiredByDate"
            class="input"
            type="text"
            disabled
            [id]="tid('complete-by')"
            [attr.data-testid]="tid('complete-by')"
            [value]="desiredByDate | date : 'MM/dd/yyyy' : locale"
          />
        </div>
      </div>

      <div class="pl-actions">
        <button
          type="button"
          class="btn btn--primary"
          [attr.aria-disabled]="productLocked || submitBidDisabled"
          [matTooltip]="productLocked ? lockedReason : submitQuoteDisabledReason"
          [matTooltipDisabled]="!productLocked && !submitBidDisabled"
          matTooltipTouchGestures="off"
          [attr.data-testid]="tid('submit-quote')"
          (click)="submitQuote()"
        >
          Submit Quote
        </button>
        <button
          type="button"
          class="btn btn--secondary"
          [attr.data-testid]="tid('decline')"
          (click)="openDeclineModal()"
          [attr.aria-disabled]="productLocked"
          [matTooltip]="lockedReason"
          [matTooltipDisabled]="!productLocked"
          matTooltipTouchGestures="off"
        >
          Decline to Quote
        </button>
      </div>
    </div>

    <!-- OFFERED → Accept / Decline at the offered price. -->
    <div class="pl-offer" *ngIf="isOffered">
      <div class="pl-read">
        <span class="pl-read__item"
          >Offer Amount:
          {{ quotedAmount | currency : "USD" : "symbol" : "1.0-0" }}</span
        >
        <span class="pl-read__item" *ngIf="offerDueDate"
          >Due Date: {{ offerDueDate | date : "MM/dd/yyyy" : locale }}</span
        >
      </div>
      <div class="pl-actions">
        <button
          type="button"
          class="btn btn--primary"
          [attr.data-testid]="tid('accept')"
          (click)="openAcceptOffer()"
          [attr.aria-disabled]="productLocked"
          [matTooltip]="lockedReason"
          [matTooltipDisabled]="!productLocked"
          matTooltipTouchGestures="off"
        >
          Accept Offer
        </button>
        <button
          type="button"
          class="btn btn--secondary"
          [attr.data-testid]="tid('counter')"
          (click)="openCounterOffer()"
          [attr.aria-disabled]="productLocked"
          [matTooltip]="lockedReason"
          [matTooltipDisabled]="!productLocked"
          matTooltipTouchGestures="off"
        >
          Counter Price
        </button>
        <button
          type="button"
          class="btn btn--link"
          [attr.data-testid]="tid('decline')"
          (click)="openDeclineModal()"
          [attr.aria-disabled]="productLocked"
          [matTooltip]="lockedReason"
          [matTooltipDisabled]="!productLocked"
          matTooltipTouchGestures="off"
        >
          Decline
        </button>
      </div>
    </div>

    <!-- COUNTERED → the countered state (mock B3): both figures, and NO
         actions at all. The PA already answered; the next move is staffing's,
         which either answers with a revised fee (→ RE_OFFERED, where the
         counter action returns) or awards elsewhere. One counter per offer,
         so there is nothing to revise from here. -->
    <div class="pl-countered" *ngIf="isCountered">
      <div class="pl-read">
        <span class="pl-read__item"
          >Offered
          {{ quotedAmount | currency : "USD" : "symbol" : "1.0-0" }}</span
        >
        <span class="pl-read__item pl-read--strong"
          >Your counter:
          {{
            counterFeeValue | currency : "USD" : "symbol" : "1.0-0"
          }}</span
        >
        <span class="pl-read__item" *ngIf="counteredAt"
          >Submitted {{ counteredAt | date : "MM/dd/yyyy" : locale }}</span
        >
      </div>
      <p class="pl-read pl-read--muted">Counter submitted — awaiting staffing</p>
    </div>

    <!-- RE_OFFERED → staffing answered the counter with a revised fee. Both
         figures again, but the roles are reversed against the COUNTERED block
         above: `quotedAmount` (`bid`) is now staffing's revised offer and
         `counterFeeValue` the PA's previous ask. The full accept / counter /
         decline matrix is back — a re-offer reopens countering, so the PA can
         answer once more. -->
    <div class="pl-re-offered" *ngIf="isReOffered">
      <div class="pl-read">
        <span class="pl-read__item pl-read--strong"
          >Revised offer:
          {{ quotedAmount | currency : "USD" : "symbol" : "1.0-0" }}</span
        >
        <span class="pl-read__item" *ngIf="counterFeeValue"
          >You asked:
          {{ counterFeeValue | currency : "USD" : "symbol" : "1.0-0" }}</span
        >
        <span class="pl-read__item" *ngIf="offerDueDate"
          >Due Date: {{ offerDueDate | date : "MM/dd/yyyy" : locale }}</span
        >
      </div>
      <p class="pl-read pl-read--muted">
        Staffing answered your counter — accept, counter again, or decline.
      </p>
      <div class="pl-actions">
        <button
          type="button"
          class="btn btn--primary"
          [attr.data-testid]="tid('accept')"
          (click)="openAcceptOffer()"
          [attr.aria-disabled]="productLocked"
          [matTooltip]="lockedReason"
          [matTooltipDisabled]="!productLocked"
          matTooltipTouchGestures="off"
        >
          Accept Offer
        </button>
        <button
          type="button"
          class="btn btn--secondary"
          [attr.data-testid]="tid('counter')"
          (click)="openCounterOffer()"
          [attr.aria-disabled]="productLocked"
          [matTooltip]="lockedReason"
          [matTooltipDisabled]="!productLocked"
          matTooltipTouchGestures="off"
        >
          Counter Again
        </button>
        <button
          type="button"
          class="btn btn--link"
          [attr.data-testid]="tid('decline')"
          (click)="openDeclineModal()"
          [attr.aria-disabled]="productLocked"
          [matTooltip]="lockedReason"
          [matTooltipDisabled]="!productLocked"
          matTooltipTouchGestures="off"
        >
          Decline
        </button>
      </div>
    </div>

    <!-- SUBMITTED / QUOTED → read-only "awaiting award" + Withdraw. Edit-bid is
         omitted: the backend locks bids after submission (BidLockedException),
         so the only permitted post-submission action is withdrawing (decline). -->
    <div class="pl-submitted" *ngIf="isSubmitted">
      <p class="pl-read pl-read--muted">
        Quoted {{ quotedAmount | currency : "USD" : "symbol" : "1.0-0" }}
        <span *ngIf="quotedTurnTime > 0">
          · {{ quotedTurnTime }} business days</span
        >
        — awaiting award
      </p>
      <div class="pl-actions">
        <button
          type="button"
          class="btn btn--secondary"
          [attr.data-testid]="tid('withdraw')"
          (click)="openDeclineModal()"
          [attr.aria-disabled]="productLocked"
          [matTooltip]="lockedReason"
          [matTooltipDisabled]="!productLocked"
          matTooltipTouchGestures="off"
        >
          Withdraw
        </button>
      </div>
    </div>

    <!-- BID_WON → Awarded + inline ATP sign. The awarded state + trigger are
         inline; the typed-signature + terms + document review reuse the existing
         signatp-dialog (its restyle is Spec 8). Signing flips it to Active. -->
    <div class="pl-awarded" *ngIf="isAwarded">
      <span class="pl-read pl-read--strong"
        >Sign the ATP to authorize and begin work.</span
      >
      <div class="pl-actions">
        <button
          type="button"
          class="btn btn--primary"
          [attr.data-testid]="tid('atp-sign')"
          (click)="generateATP()"
          [attr.aria-disabled]="productLocked"
          [matTooltip]="lockedReason"
          [matTooltipDisabled]="!productLocked"
          matTooltipTouchGestures="off"
        >
          Sign ATP
        </button>
      </div>
    </div>

    <!-- BID_LOST / NO_RESPONSE → muted, read-only. -->
    <div class="pl-not-awarded" *ngIf="isNotAwarded">
      <span class="pl-read pl-read--muted">Not awarded</span>
    </div>

    <!-- ACTIVE / ADDRESSING_COMMENTS / REVIEW_* / REVISION_* →
         status pill (in the head) + a full-width progress stepper. The
         report-writer (Quire) link now lives only in the workspace. -->
    <div class="pl-active" *ngIf="isActiveVariant">
      <!-- Compact status/timestamp-derived stepper (replaces the dormant
           percent bar — see product-progress.util.ts). One segment per
           applicable stage (5 without a required site visit, 7 with),
           stretched full-width; current-stage label (+ hint) below. -->
      <div class="pl-stepper" [attr.data-testid]="tid('stepper')">
        <div
          class="pl-stepper__segments"
          role="progressbar"
          aria-label="Deliverable progress"
          aria-valuemin="0"
          aria-valuemax="100"
          [attr.aria-valuenow]="progress.percent"
          [attr.aria-valuetext]="progress.currentLabel ?? 'Completed'"
        >
          <span
            *ngFor="let stage of progress.stages; trackBy: trackStage"
            class="pl-stepper__segment"
            [ngClass]="'pl-stepper__segment--' + stage.state"
            [attr.data-testid]="segmentTestid(stage)"
            [attr.data-state]="stage.state"
            [title]="stage.label"
            aria-hidden="true"
          ></span>
        </div>
        <span
          class="pl-stepper__label"
          [attr.data-testid]="tid('stepper-current-label')"
        >
          <ng-container
            *ngIf="progress.currentLabel as current; else stepperDone"
          >
            <span class="pl-stepper__next">Next step</span>
            <span class="pl-stepper__stage">{{ current }}</span>
            <span *ngIf="stageHint" class="pl-stepper__hint"
              >&middot; {{ stageHint }}</span
            >
          </ng-container>
          <ng-template #stepperDone>
            <span class="pl-stepper__stage pl-stepper__stage--done"
              >Completed</span
            >
          </ng-template>
        </span>
      </div>
    </div>
  </div>
  </ng-container>

  <!-- Task 3 (Project History): read-only completed summary — due date + bid
       amount (same fields/fallback as the legacy `project-history-table`
       row) and a Documents affordance (the 1.0 inline Documents icon; Notes
       + Expenses stay hidden per the spec). No write controls here. -->
  <div class="pl-completed" *ngIf="readOnly">
    <p class="pl-read">
      <span class="pl-read__item" *ngIf="completedDueDate"
        >Due Date: {{ completedDueDate | date : "MM/dd/yyyy" : locale }}</span
      >
      <span class="pl-read__item"
        >Bid Amount:
        {{ completedBidAmount | currency : "USD" : "symbol" : "1.0-0" }}</span
      >
    </p>
    <button
      type="button"
      class="pl-completed__documents"
      (click)="toggleReadOnlyDocumentsModal()"
      [attr.data-testid]="tid('documents')"
    >
      <!-- View-only history: a `folder` reads as "open documents", not `upload`
           (an ↑ arrow implied the vendor could still add files here). -->
      <span class="material-icons">folder</span>
      <span *ngIf="readOnlyDocuments.length > 0" class="pl-completed__documents-count">{{
        readOnlyDocuments.length
      }}</span>
      Documents
    </button>
  </div>
</div>

<documents-dialog
  *ngIf="readOnly && readOnlyDocumentsActive"
  [data]="product.siteDetail"
  [active]="readOnlyDocumentsActive"
  [documents]="readOnlyDocuments"
  [siteType]="siteType"
  [siteSubType]="siteSubType"
  [address]="address"
  (modalClosed)="toggleReadOnlyDocumentsModal()"
  (update)="onReadOnlyDocumentsUpdate($event)"
  [ngClass]="{ 'documents-dialog--active': readOnlyDocumentsActive }"
></documents-dialog>

<!-- Reused pre-award / award ACTION dialogs (triggered functionally; Spec 8
     restyles). The post-write CONFIRMATION dialogs (offer-accepted,
     decline-success, atp-sign-accepted) are hosted at the page level via
     ModalService — a list refresh destroys this component, and the actioned
     item often leaves the current screen's status filter, so an inline
     confirmation would never render. Task 3: never rendered in read-only
     mode — their triggers all live inside `.product-line__actions`, which is
     itself gone when `readOnly`, so this only avoids needlessly
     instantiating them. -->
<bid-dialog
  *ngIf="!readOnly && bidActive"
  [active]="bidActive"
  [address]="address"
  [data]="writeProjectDetail"
  (modalClosed)="toggleBidModal()"
  [ngClass]="{ 'documents-dialog--active': bidActive }"
></bid-dialog>

<accept-offered-proposal-dialog
  *ngIf="!readOnly && isOfferAcceptedActive"
  [active]="isOfferAcceptedActive"
  [data]="writeProjectDetail"
  [siteType]="siteType"
  [siteSubType]="siteSubType"
  [address]="address"
  [multiple]="false"
  (modalClosed)="toggleOfferAcceptedModal()"
  (acceptSingleOffer)="onAcceptSingleOffer()"
  [ngClass]="{ 'documents-dialog--active': isOfferAcceptedActive }"
></accept-offered-proposal-dialog>

<!-- Task B4 — Counter Price (OFFERED) / Counter Again (RE_OFFERED). Seeded
     from the PA's previous counter so a second round starts from it, not
     blank. -->
<counter-offer-dialog
  *ngIf="!readOnly && isCounterActive"
  [active]="isCounterActive"
  [offeredFee]="quotedAmount"
  [initialCounterFee]="counterFeeValue"
  [initialCounterCompleteBy]="counterCompleteBy"
  [initialCounterNote]="counterNote ?? ''"
  [siteType]="siteType"
  [siteSubType]="siteSubType"
  [address]="address"
  (modalClosed)="toggleCounterOfferModal()"
  (counterOffer)="counterOffer($event)"
  [ngClass]="{ 'documents-dialog--active': isCounterActive }"
></counter-offer-dialog>

<decline-dialog
  *ngIf="!readOnly && isDeclineActive"
  [active]="isDeclineActive"
  [siteType]="siteType"
  [siteSubType]="siteSubType"
  [address]="address"
  [isFixedPrice]="isFixedPriced"
  [isAtp]="isAtp"
  (modalClosed)="toggleCloseDeclineModal()"
  (decline)="decline($event)"
  [ngClass]="{ 'documents-dialog--active': isDeclineActive }"
></decline-dialog>

<signatp-dialog
  [locked]="productLocked"
  *ngIf="!readOnly && signAtpActive"
  [atpDocumentPath]="atpDocumentPath"
  [active]="signAtpActive"
  [siteType]="siteType"
  [address]="address"
  [modalHeaderTitle]="childModalHeaderTitle"
  [modalBody]="childModalBody"
  (modalClosed)="toggleATPModal()"
  (signedAtpEvent)="signATP($event)"
  (declineAtpEvent)="declineATP($event)"
  [ngClass]="{ 'documents-dialog--active': signAtpActive }"
></signatp-dialog>
